import React, { useEffect,useState } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import _uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';

import { dispatchSessionShowMoreSignupModal } from 'store/modals/actions';

import { ENTITY } from './chartUtils';
import SalaryChartItem from './SalaryChartItem';

import classes from './index.module.css';

const propTypes = {
  chartType: PropTypes.string.isRequired,
  salaryChartItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  filteredItems: PropTypes.arrayOf(PropTypes.object),
  filteredBy: PropTypes.string, // required only when a setion has filter and should be choosen from ENTITY constant
  tableHeaders: PropTypes.func,
  isDesktop: PropTypes.bool,
  showSignUp: PropTypes.bool,
};

const defaultProps = {
  chartType: '',
  filteredBy: '',
  filteredItems: [],
  salaryChartItems: [],
  isDesktop: undefined,
  tableHeaders: undefined,
  headers: undefined,
  showSignUp: false,
};

const NewSalaryChartComponent = ({
  chartType,
  salaryChartItems,
  filteredBy,
  filteredItems,
  isDesktop,
  showSignUp,
  tableHeaders,
  listSizeValue,
  tableHeaderTitleContent,
}) => {
  const dispatch = useDispatch();

  const [listSize, setListSize] = useState(listSizeValue || 6);

  const [sortedSalaryChartItems, setSortedSalaryChartItems] = useState(
    salaryChartItems.sort((a, b) => +b.median - +a.median)
  );

  useEffect(() => {
    setSortedSalaryChartItems(salaryChartItems.sort((a, b) => +b.median - +a.median));
  }, [salaryChartItems]);

  function findIndex(salaryChartItems) {
    let index = -1;
    switch (filteredBy) {
      case ENTITY.COMPANY:
        index = salaryChartItems.findIndex(
          (salaryChartItem) => salaryChartItem.companyNameCPS === filteredItems[0].companyNameCPS
        );
        break;
      default:
        index = salaryChartItems.findIndex(
          (salaryChartItem) => salaryChartItem.title === filteredItems[0].title
        );
    }
    return index;
  }

  function setListSizeOnIndexChange(index) {
    if (index > -1 && index <= 19) {
      if (index > 5 && index < 12) {
        setListSize(12);
      } else if (index >= 12) {
        setListSize(20);
      }
    } else if (index === 20) {
      setListSize(20);
    }
  }

  function sortAndArrangeSalaryItems() {
    if (!filteredItems.length) {
      setListSize(6);
      return salaryChartItems.sort((a, b) => +b.median - +a.median);
    } else if (filteredItems.length > 1) {
      setListSize(6);
      return filteredItems.sort((a, b) => +b.median - +a.median);
    }

    // Mix
    let allSalaryChartItems = [
      ...filteredItems.map((salaryChartItem) => ({
        ...salaryChartItem,
        disabled: false,
      })),
      ...salaryChartItems.map((salaryChartItem) => ({
        ...salaryChartItem,
        disabled: true,
      })),
    ];

    // Unify
    allSalaryChartItems = _uniqBy(allSalaryChartItems, (salaryChartItem) => {
      switch (filteredBy) {
        case ENTITY.COMPANY:
          return salaryChartItem.companyNameCPS;
        default:
          return salaryChartItem.title;
      }
    });

    // Sort
    allSalaryChartItems = allSalaryChartItems.sort((a, b) => +b.median - +a.median);

    // Find Index
    const index = findIndex(allSalaryChartItems);

    // Rearrange
    setListSizeOnIndexChange(index);

    if (index === 20) {
      allSalaryChartItems = [...allSalaryChartItems.slice(0, 19), allSalaryChartItems[index]];
    }
    return allSalaryChartItems.slice(0, 20);
  }

  useEffect(() => {
    if (filteredItems.length) {
      setSortedSalaryChartItems(sortAndArrangeSalaryItems());
    } else {
      setSortedSalaryChartItems(salaryChartItems.sort((a, b) => +b.median - +a.median));
    }
  }, [filteredItems]);

  const getMaxAndMinValues = () => {
    const minPercentLow = Math.min(...sortedSalaryChartItems.map((item) => item.percent25));
    const maxPercentHigh = Math.max(...sortedSalaryChartItems.map((item) => item.percent75));
    return { minPercentLow, maxPercentHigh };
  };

  const handleShowMore = () => {
    let newListSize = listSize;
    switch (listSize) {
      case 6:
        newListSize += 6;
        break;
      default:
        newListSize = sortedSalaryChartItems.length;
    }
    setListSize(newListSize);
    if (showSignUp) dispatch(dispatchSessionShowMoreSignupModal());
  };

  return (
    <div className="w-730">
      {tableHeaderTitleContent}
      <table className={cn(classes['new-salary-chart-container'])}>
        {tableHeaders && tableHeaders()}
        <tbody>
          {sortedSalaryChartItems.map((salaryChartItem, idx) => (
            <SalaryChartItem
              key={idx}
              index={idx + 1}
              type={chartType}
              salaryChartItem={salaryChartItem}
              isDesktop={isDesktop}
              isHidden={idx > listSize - 1}
              {...getMaxAndMinValues()}
            />
          ))}
        </tbody>
      </table>
      {listSize < sortedSalaryChartItems.length && sortedSalaryChartItems.length > 6 && (
        <div className="mt-4">
          <a className={cn('ShowMoreText', classes['show-more-link'])} onClick={handleShowMore}>
            Show More
          </a>
        </div>
      )}
    </div>
  );
};

NewSalaryChartComponent.propTypes = propTypes;
NewSalaryChartComponent.defaultProps = defaultProps;

export default NewSalaryChartComponent;
